import { useEffect, useMemo, useState } from "react";
import {
    Activity,
    CalendarCheck2,
    CheckCircle2,
    ClipboardList,
    Gauge,
    TrendingUp,
    Users,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";

const clamp = (value) => Math.max(0, Math.min(100, Number(value) || 0));

const getScoreTone = (score) => {
    if (score >= 80) return "text-emerald-500 bg-emerald-500/10 border-emerald-500/20";
    if (score >= 55) return "text-amber-500 bg-amber-500/10 border-amber-500/20";
    return "text-red-500 bg-red-500/10 border-red-500/20";
};

const DepartmentEfficiency = () => {
    const navigate = useNavigate();
    const [teams, setTeams] = useState([]);
    const [stats, setStats] = useState({
        activeEmployees: 0,
        checkedIn: 0,
        submittedEod: 0,
        tasks: 0,
        completedTasks: 0,
    });
    const [, setLoading] = useState(true);

    useEffect(() => {
        document.title = "Department Efficiency | Savrion Technologies";
        const fetchEfficiency = async () => {
            try {
                const res = await api.get("/api/admin/dashboard-summary");
                setTeams([...(res.data.teamStats || [])].sort((a, b) =>
                    String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" }),
                ));
                setStats(res.data.stats || {});
            } catch (err) {
                console.error(err);
                if (err.response?.status === 401 || err.response?.status === 403) {
                    navigate("/admin/login");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchEfficiency();
    }, [navigate]);

    const rankedTeams = useMemo(() => {
        return [...teams].sort((a, b) => {
            const scoreDifference = (b.efficiencyScore || 0) - (a.efficiencyScore || 0);
            if (scoreDifference !== 0) return scoreDifference;
            return String(a.name || "").localeCompare(String(b.name || ""), undefined, { sensitivity: "base" });
        });
    }, [teams]);

    const averageEfficiency = useMemo(() => {
        const teamsWithPeople = teams.filter((team) => team.active > 0);
        if (!teamsWithPeople.length) return 0;
        const total = teamsWithPeople.reduce((sum, team) => sum + (team.efficiencyScore || 0), 0);
        return Math.round(total / teamsWithPeople.length);
    }, [teams]);

    const taskCompletionRate =
        stats.tasks > 0 ? Math.round(((stats.completedTasks || 0) / stats.tasks) * 100) : 0;

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <AdminNavbar />

            <main className="flex-1 min-w-0 w-full px-5 py-5 space-y-5 fade-in overflow-auto">
                <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
                    <div>
                        <p className="text-xs font-black uppercase tracking-widest text-[var(--accent-primary)]">
                            Department intelligence
                        </p>
                        <h1 className="text-2xl md:text-3xl font-black tracking-tight mt-1">
                            Department Efficiency
                        </h1>
                        <p className="text-sm text-[var(--text-secondary)] mt-2">
                            Attendance, task completion, and EOD discipline across every department.
                        </p>
                    </div>
                    <div className={`rounded-2xl border px-5 py-4 ${getScoreTone(averageEfficiency)}`}>
                        <p className="text-[10px] font-black uppercase tracking-widest">Overall Efficiency</p>
                        <p className="text-3xl font-black mt-1">{averageEfficiency}%</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                    <MetricCard
                        label="Active Employees"
                        value={stats.activeEmployees || 0}
                        icon={<Users size={20} />}
                        tone="text-[var(--accent-primary)] bg-[var(--accent-primary)]/10"
                    />
                    <MetricCard
                        label="Checked In"
                        value={stats.checkedIn || 0}
                        icon={<CalendarCheck2 size={20} />}
                        tone="text-emerald-500 bg-emerald-500/10"
                    />
                    <MetricCard
                        label="Task Completion"
                        value={`${taskCompletionRate}%`}
                        icon={<ClipboardList size={20} />}
                        tone="text-[var(--accent-primary)] bg-[var(--accent-subtle)]"
                    />
                    <MetricCard
                        label="EOD Submitted"
                        value={stats.submittedEod || 0}
                        icon={<CheckCircle2 size={20} />}
                        tone="text-amber-500 bg-amber-500/10"
                    />
                </div>

                {rankedTeams.length === 0 ? (
                    <div className="glass-card rounded-2xl border border-[var(--border-color)] p-10 text-center">
                        <p className="text-sm font-semibold text-[var(--text-secondary)]">No department data available.</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 2xl:grid-cols-3 gap-4">
                        <div className="2xl:col-span-2 glass-card rounded-2xl border border-[var(--border-color)] overflow-hidden">
                            <div className="p-5 border-b border-[var(--border-color)] flex items-center justify-between">
                                <div>
                                    <h2 className="text-base font-black flex items-center gap-2">
                                        <Gauge size={18} className="text-[var(--accent-primary)]" />
                                        Department Scoreboard
                                    </h2>
                                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                                        Efficiency score averages attendance, task completion, and EOD compliance.
                                    </p>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full min-w-[780px] text-sm">
                                    <thead>
                                        <tr className="text-left text-[10px] uppercase tracking-widest text-[var(--text-secondary)] bg-[var(--bg-primary)]">
                                            <th className="px-5 py-3 font-black">Department</th>
                                            <th className="px-5 py-3 font-black">Efficiency</th>
                                            <th className="px-5 py-3 font-black">Attendance</th>
                                            <th className="px-5 py-3 font-black">Tasks</th>
                                            <th className="px-5 py-3 font-black">EOD</th>
                                            <th className="px-5 py-3 font-black">Team</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rankedTeams.map((team) => (
                                            <tr key={team.name} className="border-t border-[var(--border-color)]">
                                                <td className="px-5 py-4">
                                                    <p className="font-black capitalize">{team.name}</p>
                                                    <p className="text-xs text-[var(--text-secondary)] mt-1">
                                                        {team.tasks?.completed || 0} of {team.tasks?.total || 0} tasks completed
                                                    </p>
                                                </td>
                                                <td className="px-5 py-4">
                                                    <ScorePill score={team.efficiencyScore} />
                                                </td>
                                                <td className="px-5 py-4">
                                                    <Progress value={team.attendanceRate} />
                                                </td>
                                                <td className="px-5 py-4">
                                                    <Progress value={team.taskCompletionRate} />
                                                </td>
                                                <td className="px-5 py-4">
                                                    <Progress value={team.eodComplianceRate} />
                                                </td>
                                                <td className="px-5 py-4 font-bold">
                                                    {team.checkedIn || 0}/{team.active || 0}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div className="space-y-4">
                            <div className="glass-card rounded-2xl border border-[var(--border-color)] p-5">
                                <h2 className="text-base font-black flex items-center gap-2 mb-4">
                                    <TrendingUp size={18} className="text-emerald-500" />
                                    Top Departments
                                </h2>
                                <div className="space-y-3">
                                    {rankedTeams.slice(0, 5).map((team, index) => (
                                        <div key={team.name} className="rounded-xl border border-[var(--border-color)] bg-[var(--bg-primary)] p-4">
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="min-w-0">
                                                    <p className="text-[10px] font-black text-[var(--accent-primary)]">#{index + 1}</p>
                                                    <h3 className="font-black capitalize truncate">{team.name}</h3>
                                                </div>
                                                <ScorePill score={team.efficiencyScore} compact />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="glass-card rounded-2xl border border-[var(--border-color)] p-5">
                                <h2 className="text-base font-black flex items-center gap-2 mb-4">
                                    <Activity size={18} className="text-[var(--accent-primary)]" />
                                    Efficiency Mix
                                </h2>
                                <div className="space-y-3 text-xs">
                                    <Legend label="Attendance" value="Checked-in active employees today" />
                                    <Legend label="Tasks" value="Completed tasks against assigned tasks" />
                                    <Legend label="EOD" value="Submitted EODs against active employees" />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>
        </div>
    );
};

const MetricCard = ({ label, value, icon, tone }) => (
    <div className="glass-card rounded-2xl p-5 border border-[var(--border-color)]">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tone}`}>
            {icon}
        </div>
        <p className="text-3xl font-black mt-4">{value}</p>
        <p className="text-[10px] font-black uppercase tracking-widest text-[var(--text-secondary)] mt-1">
            {label}
        </p>
    </div>
);

const Progress = ({ value }) => {
    const percent = clamp(value);

    return (
        <div className="min-w-[140px]">
            <div className="flex items-center justify-between text-xs font-bold mb-1">
                <span>{percent}%</span>
            </div>
            <div className="h-2 rounded-full bg-[var(--bg-primary)] border border-[var(--border-color)] overflow-hidden">
                <div
                    className="h-full rounded-full bg-[var(--accent-primary)] transition-all"
                    style={{ width: `${percent}%` }}
                />
            </div>
        </div>
    );
};

const ScorePill = ({ score = 0, compact = false }) => (
    <span className={`inline-flex items-center justify-center rounded-xl border font-black ${getScoreTone(score)} ${compact ? "px-3 py-1 text-sm" : "px-4 py-2 text-base"}`}>
        {clamp(score)}%
    </span>
);

const Legend = ({ label, value }) => (
    <div className="rounded-xl bg-[var(--bg-primary)] border border-[var(--border-color)] p-3">
        <p className="font-black text-[var(--text-primary)]">{label}</p>
        <p className="text-[var(--text-secondary)] mt-1">{value}</p>
    </div>
);

export default DepartmentEfficiency;


